<script setup lang="ts">
import type { SearchItemClickedType } from "~/analytics/segment";
import { CategoryTrackingMapTypes, SearchResultTypes } from "~/types/Search";

type Props = {
  searchTerm: string;
};

const props = defineProps<Props>();

const searchTerm = computed(() => props.searchTerm);
const { data: searchResults, isLoading } = useAllSearchResultsQuery(searchTerm);

const { searchItemClicked } = useAnalytics();
const onSearchItemClicked = (type: SearchItemClickedType, name: string) => {
  searchItemClicked({
    type,
    name,
    tab_origin: SearchResultTypes.TopResults,
    search_term: searchTerm.value,
  });
};
</script>

<template>
  <BaseLoadingSpinner
    v-if="isLoading || !searchResults"
    logo-type="lightMode"
  />
  <template v-else>
    <LazyBasePlaceholderNoResults
      v-if="!searchResults.hasResults"
      class="m-12"
      variant="searchPage"
    />
    <SearchResultPageCategories
      v-if="searchResults.teamCategories.length > 0"
      :category-results="searchResults.teamCategories"
      :category-type="SearchResultTypes.Teams"
      @search-item-clicked="
        (category, type) =>
          onSearchItemClicked(CategoryTrackingMapTypes[type], category.name)
      "
    />
    <SearchResultPageEvents
      v-if="searchResults.events.length > 0"
      :event-results="searchResults.events"
      @search-item-clicked="(event) => onSearchItemClicked('event', event.name)"
    />
    <div class="block-container">
      <SearchResultPageSubHeader
        v-if="searchResults.products.length > 0"
        :type="SearchResultTypes.Products"
        class="uppercase mb-4 px-0 site-viewport-container pt-6"
      />
      <ProductSectionProductsGrid
        v-if="searchResults.products.length > 0"
        :products="searchResults.products"
        tracking-source="searchPageAllResults"
        class="site-viewport-container pb-4 border-b"
        @product-clicked="
          (product) => onSearchItemClicked('product', product.name)
        "
      />
    </div>
    <SearchResultPageCategories
      v-if="searchResults.leagueCategories.length > 0"
      :category-results="searchResults.leagueCategories"
      :category-type="SearchResultTypes.Leagues"
      @search-item-clicked="
        (category, type) =>
          onSearchItemClicked(CategoryTrackingMapTypes[type], category.name)
      "
    />
    <SearchResultPageCategories
      v-if="searchResults.otherCategories.length > 0"
      :category-results="searchResults.otherCategories"
      :category-type="SearchResultTypes.Other"
      @search-item-clicked="
        (category, type) =>
          onSearchItemClicked(CategoryTrackingMapTypes[type], category.name)
      "
    />
  </template>
</template>
